import {
  CalendarOutlined,
  FallOutlined,
  InfoCircleOutlined,
  RiseOutlined,
  PauseCircleOutlined,
  PieChartOutlined,
  TableOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import React, { FC, useState } from 'react';

import { useOperationsDashboardContext } from '../../contexts/OperationsDashboardContext';
import { IHealthFlagResult } from '../../utils/healthFlags';
import styles from '../OperationsDashboard/ops-theme.module.css';
import OpsMetricDetailModal, {
  IOpsMetricDetailConfig,
} from '../OpsMetricDetailModal';

const FLAG_ICONS: Record<string, typeof CalendarOutlined> = {
  newRegistration: CalendarOutlined,
  pipelineStall: PauseCircleOutlined,
  potentialReg: PieChartOutlined,
  unitMom: FallOutlined,
};

const FLAG_DESCRIPTIONS: Record<string, React.ReactNode> = {
  newRegistration: (
    <>
      <strong>No New Registration</strong> tracks how many days have elapsed
      since the last new donor was registered at this location. A long gap
      indicates recruiting has stalled and fresh pipeline is not being built.
      Regular new registrations are critical for long-term donor volume.
    </>
  ),
  pipelineStall: (
    <>
      <strong>Pipeline Stall</strong> measures the average number of days that
      Pending donors at this location have been waiting without advancing to
      Approved or Active status. High values indicate a processing or engagement
      bottleneck that is slowing down donor conversion.
    </>
  ),
  potentialReg: (
    <>
      <strong>Potential Registrations</strong> compares the current active donor
      count to the estimated total eligible donor pool at this location. Low
      penetration means there is untapped capacity — more recruiting at this
      site could yield more donations without opening new locations.
    </>
  ),
  unitMom: (
    <>
      <strong>Unit Month-over-Month</strong> tracks the percentage change in
      pRBC units collected this period versus the prior equivalent period. A
      declining trend is an early warning sign of reduced donor frequency,
      increased deferrals, or shrinking active donor count.
    </>
  ),
};

const FLAG_FORMULAS: Record<string, string> = {
  newRegistration: 'Days Since Last Reg = TODAY − MAX(registration date)',
  pipelineStall:
    'Avg Pipeline Days = SUM(days in Pending) ÷ Count of Pending Donors',
  potentialReg: 'Potential Reg % = Active Donors ÷ Estimated Donor Pool × 100',
  unitMom:
    'Unit MoM % = (Current Period Units − Prior Period Units) ÷ Prior Period Units × 100',
};

const STATUS_CLASS: Record<HealthFlagStatus, string> = {
  up: styles.healthFlagCardUp,
  warn: styles.healthFlagCardWarn,
  down: styles.healthFlagCardDown,
};

function buildFlagModalRows(
  flag: IHealthFlagResult
): IOpsMetricDetailConfig['rows'] {
  const d = flag.detail;

  switch (flag.key) {
    case 'newRegistration': {
      const donorName = d?.lastNewDonorName;
      const donorDate = d?.lastNewDonorDate
        ? new Date(d.lastNewDonorDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : null;
      return [
        {
          label: 'Days since last reg',
          value: flag.value,
          bold: true,
        },
        ...(donorName
          ? [{ label: 'Last registered donor', value: donorName, bold: true }]
          : []),
        ...(donorDate
          ? [{ label: 'Registration date', value: donorDate }]
          : []),
        ...(!donorName && !donorDate
          ? [{ label: 'Last donor', value: 'No data' }]
          : []),
      ];
    }

    case 'pipelineStall': {
      const stalled = d?.stalledDonors ?? [];
      const baseRows: IOpsMetricDetailConfig['rows'] = [
        {
          label: 'Avg days pending',
          value: flag.value,
          bold: true,
        },
        {
          label: 'Pending donors',
          value: stalled.length > 0 ? stalled.length : 'None',
          bold: true,
        },
      ];
      const donorRows: IOpsMetricDetailConfig['rows'] = stalled.map((s) => ({
        label: s.name,
        value: `${s.daysPending}d`,
        indent: true,
        color:
          s.daysPending >= 60
            ? '#E8008A'
            : s.daysPending >= 30
            ? '#c6c300'
            : '#00E5A0',
      }));
      return [...baseRows, ...donorRows];
    }

    case 'potentialReg': {
      const active = d?.activeDonors;
      const pool = d?.totalDonorPool;
      return [
        {
          label: 'Registration rate',
          value: flag.value,
          bold: true,
        },
        ...(pool != null
          ? [{ label: 'Total donor pool (PAWS)', value: pool, bold: true }]
          : [{ label: 'Total donor pool', value: 'No pool data' }]),
        ...(active != null
          ? [{ label: 'Registered donors', value: active }]
          : []),
        ...(pool != null && active != null
          ? [
              {
                label: 'Unregistered potential',
                value: pool - active,
                color: pool - active > 0 ? '#c6c300' : '#00E5A0',
              },
            ]
          : []),
      ];
    }

    case 'unitMom': {
      const cur = d?.currentUnits;
      const prev = d?.prevUnits;
      const diff = cur != null && prev != null ? cur - prev : null;
      return [
        {
          label: 'Period-over-period change',
          value: flag.value,
          bold: true,
        },
        ...(cur != null
          ? [{ label: 'Current period units', value: cur, bold: true }]
          : []),
        ...(prev != null
          ? [{ label: 'Prior period units', value: prev }]
          : [{ label: 'Prior period units', value: 'No prior data' }]),
        ...(diff != null
          ? [
              {
                label: 'Unit difference',
                value: `${diff >= 0 ? '+' : ''}${diff}`,
                color: diff >= 0 ? '#00E5A0' : '#E8008A',
              },
            ]
          : []),
      ];
    }

    default:
      return [
        {
          label: 'Current Value',
          value: flag.value,
          bold: true,
        },
        { label: 'Detail', value: flag.subtext },
      ];
  }
}

interface IOpsHealthFlagCardsProps {
  flags: IHealthFlagResult[];
  locationId: string;
  onDrilldown?: (flagKey: string, locationId: string) => void;
}

const OpsHealthFlagCards: FC<IOpsHealthFlagCardsProps> = ({
  flags,
  locationId,
  onDrilldown,
}) => {
  const { isDarkMode } = useOperationsDashboardContext();
  const [modalConfig, setModalConfig] = useState<IOpsMetricDetailConfig | null>(
    null
  );

  return (
    <>
      <div className={styles.healthFlagCardsRow}>
        {flags.map((flag) => {
          let Icon = FLAG_ICONS[flag.key] || WarningOutlined;
          if (flag.key === 'unitMom' && flag.status === 'up') {
            Icon = RiseOutlined;
          }
          const drilldownId = `${locationId}:${flag.key}`;
          const showDrilldown =
            flag.key !== 'potentialReg' && flag.key !== 'unitMom';
          return (
            <div
              key={flag.key}
              className={`${styles.healthFlagCard} ${
                STATUS_CLASS[flag.status]
              }`}
            >
              <div
                className={styles.summaryCardInfoTab}
                onClick={(e) => {
                  e.stopPropagation();
                  setModalConfig({
                    title: flag.label,
                    description: FLAG_DESCRIPTIONS[flag.key],
                    statusLabel:
                      flag.status === 'up'
                        ? 'Good'
                        : flag.status === 'warn'
                        ? 'Warning'
                        : 'Action Required',
                    rows: buildFlagModalRows(flag),
                    formula: FLAG_FORMULAS[flag.key],
                  });
                }}
              >
                <InfoCircleOutlined />
              </div>
              <div className={styles.healthFlagCardIcon}>
                <Icon />
              </div>
              <span className={styles.healthFlagCardLabel}>{flag.label}</span>
              <div className={styles.healthFlagCardValueRow}>
                <span className={styles.healthFlagCardValue}>{flag.value}</span>
                {showDrilldown && (
                  <button
                    type="button"
                    className={styles.healthFlagDrilldownBtn}
                    data-drilldown-id={drilldownId}
                    title="View in Drilldown"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDrilldown?.(flag.key, locationId);
                    }}
                  >
                    <TableOutlined />
                  </button>
                )}
              </div>
              <span className={styles.healthFlagCardSub}>{flag.subtext}</span>
            </div>
          );
        })}
      </div>

      <OpsMetricDetailModal
        visible={modalConfig !== null}
        onClose={() => setModalConfig(null)}
        config={modalConfig}
        isDarkMode={isDarkMode}
      />
    </>
  );
};

export default OpsHealthFlagCards;
